import { StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { Survey } from '../../../containers/Survey';
import { CustomFormMetaData, ISatisfactionSurvey, ISatisfactionSurveyResponse } from '../../../models/Api';
import { AdditionalSurveyQuestions } from '../SatisfactionSurvey/AdditionalSurveyQuestions';
import { ThankYouMessage } from '../ThankYouMessage';
import { useHandleSatisfactionSurveyResponse } from './hooks';
import { AskForReviewOption, SurveyFooter } from './presentation';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

export const SatisfactionSurveyResponseSubmitted: React.FC<IProps> = props => {
	const { className = '', styles = [] } = props;

	const h = useHistory();
	const { params: urlParams = {}, url } = useRouteMatch<{ token?: string }>();
	const { state } = useLocation<{ response?: ISatisfactionSurveyResponse; survey?: ISatisfactionSurvey }>();
	const [showThankYouMessage, setShowThankYouMessage] = React.useState<boolean>(false);
	const response = state?.response;
	const survey: ISatisfactionSurvey = state?.survey;
	const questions = survey?.customForm?.fields;

	const filteredQuestions = React.useMemo(() => {
		return questions?.filter(field => {
			const customMetaData: CustomFormMetaData = survey.customFormMetaData.find(
				(md: CustomFormMetaData) => md.fieldId === field.id
			);
			if (field.isHidden) return false;
			if (!customMetaData?.ratings) return true;
			return customMetaData.ratings.includes(response?.rating);
		});
	}, [questions, survey?.customFormMetaData, response?.rating]);

	const { requestReview, onReviewLinkClicked } = useHandleSatisfactionSurveyResponse({
		response,
		survey,
		token: urlParams.token,
	});

	if (!state?.response) {
		h.replace(`${url.replace(/\/submitted$/gim, '')}`);
		return null;
	}

	const rootElementClassName = 'satisfaction-survey-response-submitted';

	if (showThankYouMessage || (requestReview && !filteredQuestions?.length)) {
		return (
			<Survey
				className={`${css(styleSheet.container, ...styles)} ${className || ''} ${rootElementClassName}`}
				contentStyles={[styleSheet.surveyContainerContent]}
				onRenderFooter={SurveyFooter}
				survey={survey}
			>
				{requestReview ? (
					<AskForReviewOption survey={survey} onReviewLinkClicked={onReviewLinkClicked} />
				) : (
					<ThankYouMessage className={css(styleSheet.thankyou)} />
				)}
			</Survey>
		);
	}

	return (
		<Survey
			className={`${css(styleSheet.container, ...styles)} ${className || ''} ${rootElementClassName}`}
			survey={survey}
			onRenderFooter={SurveyFooter}
		>
			<AdditionalSurveyQuestions survey={survey} response={response} setShowThankYouMessage={setShowThankYouMessage} />
		</Survey>
	);
};
